import { FC, useEffect, useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";

import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { Site } from "interfaces/kpis";
import { KpiPercentIndicatorCircle } from "components/PercentIndicatorCircle/KpiPercentIndicatorCircle";
import { useKpisStore } from "stores/kpis.store";
import { useSitesProgressQuery } from "hooks/queries/kpis";
import { KpisLinearProgress } from "pages/Kpis/KpisLinearProgress";
import { getPercentageAcrossSites } from "pages/Kpis/utils/getPercentageAcrossSites";

export interface KpisListProps {
  loadingInitialProjects: boolean;
  loadingMoreProjects?: boolean;
  error: boolean;
  items: Site[];
  onScrolledToEnd: () => void;
}

export const KpisList: FC<KpisListProps> = ({
  loadingInitialProjects,
  loadingMoreProjects,
  error,
  items,
  onScrolledToEnd
}): JSX.Element => {
  const {
    selectedSite,
    setSelectedSite,
    selectedYear,
    setSelectedYear
  } = useKpisStore(
    useShallow(store => ({
      selectedSite: store.selectedSite,
      setSelectedSite: store.setSelectedSite,
      selectedYear: store.selectedYear,
      setSelectedYear: store.setSelectedYear
    }))
  );

  const queryClient = useQueryClient();

  const handleScroll = (event: React.UIEvent<HTMLElement, UIEvent>): void => {
    // if scrolled to the bottom of the container
    if (
      event.currentTarget.scrollTop ===
      event.currentTarget.scrollHeight - event.currentTarget.offsetHeight
    ) {
      onScrolledToEnd();
    }
  };

  const {
    isLoading: isSitesLoading,
    isError: isSitesError,
    data: sites
  } = useSitesProgressQuery({ page: 1, size: 999_999, ordering: "-year" });

  const {
    isLoading: isSitesForYearLoading,
    isError: isSitesForYearError,
    data: sitesForYear
  } = useSitesProgressQuery({
    page: 1,
    size: 999_999,
    ordering: "-year",
    year: selectedYear
  });

  const years = useMemo(() => {
    const siteYears = sites?.results.map(site => site.year);
    return Array.from(new Set(siteYears));
  }, [sites]);

  useEffect(() => {
    if (
      selectedYear === undefined &&
      !isSitesLoading &&
      !isSitesError &&
      sites.results.length
    ) {
      let year = new Date().getFullYear();

      // Initially we either select the current year or the latest year
      if (!years.includes(year)) {
        year = years[0];
      }

      setSelectedYear(year);
    }
  }, [
    sites?.results,
    selectedYear,
    setSelectedYear,
    isSitesLoading,
    isSitesError,
    years
  ]);

  // set selected year to the first year in the list after year has changed
  useEffect(() => {
    if (
      !isSitesForYearLoading &&
      !isSitesForYearError &&
      sitesForYear?.results.length &&
      selectedSite &&
      !items.some(item => item.id === selectedSite.id)
    ) {
      if (selectedSite !== sitesForYear.results[0]) {
        setSelectedSite(sitesForYear.results[0]);
      }
    }
  }, [
    sitesForYear,
    isSitesForYearLoading,
    isSitesForYearError,
    setSelectedSite,
    selectedSite,
    items
  ]);

  const yearlyKpiProgressPercentage = useMemo(() => {
    if (!isSitesForYearLoading && !isSitesForYearError) {
      return getPercentageAcrossSites(sitesForYear.results);
    }

    return 0;
  }, [sitesForYear, isSitesForYearLoading, isSitesForYearError]);

  if (isSitesError || isSitesForYearError) {
    return <Typography>An error occurred</Typography>;
  }

  const handleSelectSite = async (site: Site): Promise<void> => {
    const queryIds = { site_id: site.id };
    await queryClient.invalidateQueries(["kpis", "targets", queryIds]);
    await queryClient.invalidateQueries(["kpis", "reports", queryIds]);
    await queryClient.invalidateQueries([
      "kpis",
      "progress",
      "sites_kpis",
      queryIds
    ]);
    setSelectedSite(site);
  };

  return (
    <ListWrapper>
      <Box pt={1}>
        <FormControl fullWidth>
          <InputLabel id="select-year-label">Year</InputLabel>
          <Select
            data-qaid="KPI-Select-Year"
            labelId="select-year-label"
            id="select-year"
            label="Year"
            value={String(selectedYear)}
            disabled={isSitesLoading || isSitesError || !years.length}
            onChange={(event: SelectChangeEvent) => {
              setSelectedYear(Number(event.target.value));
            }}
          >
            {years.map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Tooltip
        title={`Percentage of Targets achieved across all sites for ${selectedYear}`}
      >
        <KpisLinearProgress
          value={yearlyKpiProgressPercentage}
          variant="determinate"
          data-qa="KPI-Progress-for-Site-Year"
          sx={{ width: "100%" }}
        />
      </Tooltip>

      <Typography variant="h6">Sites</Typography>

      {!!items.length && (
        <>
          <List
            sx={{
              height: "100%",
              overflowY: "auto",
              py: 0,
              width: "100%"
            }}
            onScroll={handleScroll}
            data-qa="KPI-Card-List"
          >
            {items?.map(item => {
              return (
                <ListItem key={item.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => handleSelectSite(item)}
                    selected={selectedSite && item.id === selectedSite.id}
                  >
                    <ListItemAvatar>
                      <KpiPercentIndicatorCircle
                        percent={item.progress.percentage_metric}
                        size={40}
                        strokeWidth={4}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <ListItemContent>
                          <Box>
                            <Tooltip title={item.name}>
                              <Truncate>{item.name}</Truncate>
                            </Tooltip>
                          </Box>
                        </ListItemContent>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      <ProgressIndicatorContainer
        loading={loadingInitialProjects || (loadingMoreProjects ?? false)}
        error={error}
        errorMessage="An error occured whilst trying to load your items"
      />

      {!loadingInitialProjects && !error && !items.length && (
        <Typography sx={{ textAlign: "center" }}>
          There are no items to display
        </Typography>
      )}
    </ListWrapper>
  );
};

const ListWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;
`;
ListWrapper.displayName = "ListWrapper";

const Truncate = styled("span")`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
Truncate.displayName = "Truncate";

const ListItemContent = styled("div")`
  align-items: center;
  display: grid;
  grid-template-columns: minmax(0, 1fr) min-content;
  gap: ${({ theme }) => theme.spacing(1)};
`;
ListItemContent.displayName = "ListItemContent";
